/**
 * AlertsConsole — Phase 5.D
 *
 * Active anomalies + persistent event history. The cron-driven
 * scheduled() handler in console-worker writes events to
 * R2:_console/anomaly-events.jsonl every 5 minutes; this view shows:
 *
 *   - Active anomalies (live computed from /api/anomalies)
 *   - Event history (chronological from /api/anomaly-events)
 *   - Per-anomaly fire/resolve frequency
 *   - ✨ "Explain this alert" per row
 *
 * No external dispatch yet (email / Slack) — Phase 5.D.4 will add that
 * once we know which channel matters most. The R2 log is the durable
 * source of truth.
 */
import { html, type TemplateResult } from "lit-html";
import { store } from "../data/store";
import type {
  AnomalyBadges, AnomalyBadge,
  AnomalyEventLog, AnomalyEvent,
} from "../data/types";
import { renderExplainButton } from "./ExplainButton";

type SeverityFilter = "ALL" | "info" | "warn" | "error";
type KindFilter     = "ALL" | "fired" | "resolved";

interface ViewState {
  severityFilter: SeverityFilter;
  kindFilter:     KindFilter;
  search:         string;
}
const view: ViewState = {
  severityFilter: "ALL",
  kindFilter:     "ALL",
  search:         "",
};

export function renderAlertsConsole(onChange: () => void): TemplateResult {
  const live = store.get<AnomalyBadges>("anomalies");
  const log  = store.get<AnomalyEventLog>("anomalyEvents");

  if (live.error) return errorView(live.error);
  if (log.error)  return errorView(log.error);
  if ((!live.data && live.loading) || (!log.data && log.loading)) return skeletonView();

  const activeBadges = live.data?.badges ?? [];
  const events       = log.data?.events ?? [];
  const filtered     = applyFilters(events);

  // Per-id fire counts across the full history (for trend column)
  const fireCount: Record<string, number> = {};
  for (const e of events) {
    if (e.kind === "fired") fireCount[e.id] = (fireCount[e.id] ?? 0) + 1;
  }

  return html`
    <div class="alerts">
      ${heroStrip(activeBadges, events)}
      ${activeSection(activeBadges, fireCount)}
      ${historySection(filtered, events.length, onChange)}
    </div>
  `;
}

// =============================================================
// Hero strip
// =============================================================
function heroStrip(active: AnomalyBadge[], events: AnomalyEvent[]): TemplateResult {
  const errors = active.filter((b) => b.severity === "error").length;
  const warns  = active.filter((b) => b.severity === "warn").length;

  // Last 24h fired events
  const cutoff = Date.now() - 24 * 3600_000;
  const fired24h = events.filter((e) =>
    e.kind === "fired" && Date.parse(e.ts) >= cutoff,
  ).length;
  const resolved24h = events.filter((e) =>
    e.kind === "resolved" && Date.parse(e.ts) >= cutoff,
  ).length;

  const healthCls = errors > 0 ? "is-bad"
                  : warns > 0  ? "is-warn"
                  : "is-ok";
  const healthLabel = errors > 0 ? "ERROR"
                    : warns > 0  ? "WARN"
                    : "NOMINAL";

  return html`
    <section class="panel alerts-hero">
      <div class="alerts-hero__row">
        <div class="alerts-hero__metric">
          <span class="alerts-hero__label">health</span>
          <span class="alerts-hero__pill ${healthCls}">${healthLabel}</span>
        </div>
        <div class="alerts-hero__metric">
          <span class="alerts-hero__label">active</span>
          <span class="alerts-hero__value">${active.length}</span>
          <span class="alerts-hero__sub">
            ${errors} error · ${warns} warn
          </span>
        </div>
        <div class="alerts-hero__metric">
          <span class="alerts-hero__label">fired (24h)</span>
          <span class="alerts-hero__value">${fired24h}</span>
        </div>
        <div class="alerts-hero__metric">
          <span class="alerts-hero__label">resolved (24h)</span>
          <span class="alerts-hero__value">${resolved24h}</span>
        </div>
        <div class="alerts-hero__metric">
          <span class="alerts-hero__label">total events</span>
          <span class="alerts-hero__value">${events.length}</span>
          <span class="alerts-hero__sub">cron logs every 5 min</span>
        </div>
        <span class="alerts-hero__spacer"></span>
        ${renderExplainButton({
          label: "Explain alert state",
          context: () => buildOverallExplainContext(active, events),
        })}
      </div>
    </section>
  `;
}

// =============================================================
// Active anomalies
// =============================================================
function activeSection(
  badges: AnomalyBadge[],
  fireCount: Record<string, number>,
): TemplateResult {
  if (badges.length === 0) {
    return html`
      <section class="panel alerts-active alerts-active--ok">
        <div class="alerts-active__icon">✓</div>
        <div>
          <h3>All systems nominal</h3>
          <p>No active anomalies. Cron is checking every 5 minutes.</p>
        </div>
      </section>
    `;
  }

  return html`
    <section class="panel alerts-active">
      <header class="alerts-active__head">
        <h3>Active anomalies · ${badges.length}</h3>
      </header>
      <ul class="alerts-active__list">
        ${badges.map((b) => activeRow(b, fireCount[b.id] ?? 0))}
      </ul>
    </section>
  `;
}

function activeRow(b: AnomalyBadge, lifetimeFires: number): TemplateResult {
  const sevCls = `is-${b.severity}`;
  return html`
    <li class="alerts-active-row alerts-active-row--${b.severity} ${b.severity === "error" ? "panel--hero is-error" : ""}">
      <span class="alerts-active-row__sev ${sevCls}">${b.severity}</span>
      <div class="alerts-active-row__body">
        <div class="alerts-active-row__title">${b.title}</div>
        <div class="alerts-active-row__detail">${b.detail}</div>
      </div>
      <span class="alerts-active-row__fires"
            title="Times this id has fired in the log">
        ${lifetimeFires}× total
      </span>
      ${renderExplainButton({
        label: "Explain",
        context: () => `Active anomaly:\n${b.severity}: ${b.title}\n${b.detail}\n\nThis alert has fired ${lifetimeFires} times in the persisted history.`,
      })}
    </li>
  `;
}

// =============================================================
// Event history
// =============================================================
function historySection(
  filtered: AnomalyEvent[],
  totalEvents: number,
  onChange: () => void,
): TemplateResult {
  const severities: SeverityFilter[] = ["ALL", "info", "warn", "error"];
  const kinds: KindFilter[]          = ["ALL", "fired", "resolved"];

  return html`
    <section class="panel alerts-history">
      <header class="alerts-history__head">
        <div>
          <h3>Event history · ${filtered.length}/${totalEvents}</h3>
          <span class="alerts-history__sub">newest first · cron writes every 5 min</span>
        </div>

        <input
          class="alerts-history__search"
          type="search"
          placeholder="search title, detail, id…"
          .value=${view.search}
          @input=${(e: Event) => {
            view.search = (e.target as HTMLInputElement).value;
            onChange();
          }}
        />

        <div class="alerts-history__chips">
          ${severities.map((s) => html`
            <button
              class="alerts-chip alerts-chip--sev-${s}
                     ${view.severityFilter === s ? "is-active" : ""}"
              @click=${() => { view.severityFilter = s; onChange(); }}
            >${s}</button>
          `)}
        </div>

        <div class="alerts-history__chips">
          ${kinds.map((k) => html`
            <button
              class="alerts-chip alerts-chip--kind-${k}
                     ${view.kindFilter === k ? "is-active" : ""}"
              @click=${() => { view.kindFilter = k; onChange(); }}
            >${k}</button>
          `)}
        </div>
      </header>

      ${filtered.length === 0 ? html`
        <div class="alerts-history__empty">
          ${totalEvents === 0
            ? "No events logged yet — first cron tick lands within 5 min."
            : "No events match the current filters."}
        </div>
      ` : html`
        <ol class="alerts-history__list">
          ${filtered.map(historyRow)}
        </ol>
      `}
    </section>
  `;
}

function historyRow(e: AnomalyEvent): TemplateResult {
  const ts = new Date(e.ts).toLocaleString([], {
    month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
  const kindCls = `is-${e.kind}`;
  const sevCls  = e.severity ? `is-${e.severity}` : "is-info";
  return html`
    <li class="alerts-history-row">
      <span class="alerts-history-row__ts">${ts}</span>
      <span class="alerts-history-row__kind ${kindCls}">${e.kind}</span>
      ${e.severity ? html`
        <span class="alerts-history-row__sev ${sevCls}">${e.severity}</span>
      ` : html`<span class="alerts-history-row__sev"></span>`}
      <span class="alerts-history-row__id"
            title=${e.id}>
        ${e.id.replace("agent-failed-com.yurysankov.", "")}
      </span>
      <span class="alerts-history-row__title">
        ${e.title ?? (e.kind === "resolved" ? "(resolved)" : "")}
      </span>
      <span class="alerts-history-row__detail" title=${e.detail ?? ""}>
        ${e.detail ?? ""}
      </span>
    </li>
  `;
}

// =============================================================
// Filtering
// =============================================================
function applyFilters(events: AnomalyEvent[]): AnomalyEvent[] {
  let out = events;
  if (view.severityFilter !== "ALL") {
    out = out.filter((e) => e.severity === view.severityFilter);
  }
  if (view.kindFilter !== "ALL") {
    out = out.filter((e) => e.kind === view.kindFilter);
  }
  if (view.search.trim()) {
    const q = view.search.trim().toLowerCase();
    out = out.filter((e) => {
      const hay = [e.id, e.title, e.detail].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }
  return out;
}

// =============================================================
// Explain context
// =============================================================
function buildOverallExplainContext(
  active: AnomalyBadge[],
  events: AnomalyEvent[],
): string {
  const cutoff = Date.now() - 24 * 3600_000;
  const recent = events.filter((e) => Date.parse(e.ts) >= cutoff);
  return [
    `Alert state:`,
    `Active anomalies: ${active.length} (${active.filter((b) => b.severity === "error").length} error, ${active.filter((b) => b.severity === "warn").length} warn)`,
    `Last 24h: ${recent.filter((e) => e.kind === "fired").length} fired, ${recent.filter((e) => e.kind === "resolved").length} resolved`,
    `Total events in history: ${events.length}`,
    "",
    "Active:",
    ...active.map((b) => `  [${b.severity}] ${b.id}: ${b.title} — ${b.detail}`),
    "",
    "Most recent 10 events:",
    ...events.slice(0, 10).map((e) =>
      `  ${e.ts} ${e.kind.toUpperCase()} [${e.severity ?? "—"}] ${e.id}: ${e.title ?? ""} ${e.detail ?? ""}`,
    ),
  ].join("\n");
}

// =============================================================
// State views
// =============================================================
function skeletonView(): TemplateResult {
  return html`
    <div class="alerts">
      <section class="panel" style="height:120px;"></section>
      <section class="panel" style="height:200px;"></section>
      <section class="panel" style="height:400px;"></section>
    </div>
  `;
}

function errorView(err: Error): TemplateResult {
  return html`
    <div class="panel" style="padding:32px;border-color:rgba(255,69,58,0.32);">
      <div class="panel__title">
        <span>Alerts</span>
        <span class="phase-stub-badge"
              style="background:rgba(255,69,58,0.16);
                      color:var(--accent-red-light);
                      border-color:rgba(255,69,58,0.32);">
          fetch error
        </span>
      </div>
      <div style="color:var(--accent-red-light);font-size:12px;
                   line-height:1.5;font-family:monospace;
                   word-break:break-all;white-space:pre-wrap;
                   margin-top:8px;">
        ${err.message}
      </div>
    </div>
  `;
}
